import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Briefcase } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { full_name: "", email: "", phone: "", password: "", role: "instructor", hourly_rate: "", hire_date: "" };

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/staff-and-instructors");
      setStaff(res.data);
    } catch {
      toast.error("Could not load staff");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    try {
      const payload = { ...form };
      if (payload.hourly_rate) payload.hourly_rate = parseFloat(payload.hourly_rate);
      else delete payload.hourly_rate;
      await api.post("/auth/staff", payload);
      toast.success("Account created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}/${u.is_active ? "deactivate" : "activate"}`);
      toast.success(u.is_active ? "Account deactivated" : "Account activated");
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="staff-management-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Create login accounts for office staff and driving instructors.</p>
        <Button onClick={() => setOpen(true)} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="add-staff-button">
          <Plus className="h-4 w-4 mr-2" /> Add Account
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      ) : staff.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="staff-empty-state">
          <Briefcase className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No staff or instructor accounts yet.</p>
        </div>
      ) : (
        <div className="rounded-[var(--radius)] border bg-card overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.map((u) => (
                <TableRow key={u.id} data-testid="staff-row">
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="capitalize">{u.role.replace("_", " ")}</TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">{u.email}</TableCell>
                  <TableCell>{u.is_active ? "Active" : "Inactive"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid="toggle-staff-active-button">
                      {u.is_active ? "Deactivate" : "Activate"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff or Instructor Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Role</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="staff-form-role-select">
                <option value="instructor">Driving Instructor</option>
                <option value="office_staff">Office Staff</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} data-testid="staff-form-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="staff-form-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="staff-form-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="staff-form-password-input" />
            </div>
            {form.role === "instructor" && (
              <div className="space-y-1.5">
                <Label>Hourly rate ($, optional)</Label>
                <Input type="number" value={form.hourly_rate} onChange={(e) => setForm((f) => ({ ...f, hourly_rate: e.target.value }))} data-testid="staff-form-hourly-rate-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="staff-form-submit-button">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
